<template>
  <div class="lila-chat-message">
    <div
      v-if="showMessageDate"
      class="has-text-centered"
    >
      <div class="message-date">
        {{
          $Utilities.formatDateWithTime(
            message.DateAndTime,
            "YYYY-MM-DDTHH:mm:ss",
            "DD/MM/YYYY"
          )
        }}
      </div>
    </div>

    <div
      v-if="isCallEvent"
      class="call-event-message"
    >
      <div class="call-event-message__icon">
        <svg
          viewBox="0 0 24 24"
          width="22"
          height="22"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
          />
        </svg>
      </div>
      <div class="call-event-message__body">
        <span class="call-event-message__label">{{ callEventParts.label }}</span>
        <span
          v-if="callEventParts.duration"
          class="call-event-message__duration"
        >
          {{ callEventParts.duration }}
        </span>
      </div>
      <time class="call-event-message__time">{{
        $Utilities.formatDateWithTime(
          message.DateAndTime,
          "YYYY-MM-DDTHH:mm:ss",
          "HH:mm"
        )
      }}</time>
    </div>

    <div
      v-else
      :class="
        message.Origin === 'Patient' || message.Origin === 1
          ? 'patient-message'
          : 'doctor-message'
      "
    >
      <div v-if="message.AttachedDocument">
        <p
          v-if="$Utilities.getFileExtension(message.AttachedDocument) === 'pdf'"
          class="file-link"
        >
          <a
            :href="message.AttachedDocument"
            target="_blank"
            download
          >
            Baixar PDF
          </a>
        </p>

        <figure
          v-else
          class="image"
          @click="onClick"
        >
          <img :src="message.AttachedDocument">
        </figure>
      </div>

      <p
        v-if="message.Message"
        v-html="$Utilities.addHiperlinkinText(mensagemSanitizada)"
      />

      <div class="message-footer">
        <p
          class="message-origin"
          :class="{ 'is-automatic': isAutomaticMessage }"
        >
          {{ messageOrigin }}
        </p>

        <p class="has-text-right message-meta">
          <button
            v-if="canEdit"
            type="button"
            class="borderless message-edit-btn"
            title="Editar mensagem"
            aria-label="Editar mensagem"
            @click="emit('edit', message)"
          >
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                d="M6.414,16,16.556,5.858,15.142,4.444,5,14.586V16Zm.829,2H3V13.757L14.435,2.322a1,1,0,0,1,1.414,0l2.829,2.829a1,1,0,0,1,0,1.414ZM3,20H21v2H3Z"
              />
            </svg>
          </button>
          <span
            v-if="isEdited"
            class="message-edited-tag"
            :title="editedTitle"
          >
            editada
          </span>
          <time>{{
            $Utilities.formatDateWithTime(
              message.DateAndTime,
              "YYYY-MM-DDTHH:mm:ss",
              "HH:mm"
            )
          }}</time>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import moment from "moment";
import { computed, ref } from "vue";
import DOMPurify from "dompurify"; // ✅ Adicionado
import getValueIgnoreCase from "@js/getValueIgnoreCase.js";
import useDialog from "@composables/useDialog.js";
import LilaImg from "../Common/LIlaImg.vue";
import { Utilities } from "../../plugins/Utilities.js";

const p = defineProps({
  message: null,
  lastDate: null,
  allowEdit: { type: Boolean, default: true }
});

// Avisa o pai (Chat.vue) para iniciar a edição na barra inferior.
const emit = defineEmits(["edit"]);

const { create } = useDialog();

// --- Edição de mensagem ---------------------------------------------------
// A regra de "quem pode editar" é decidida pelo backend através do campo
// canEdit; o front apenas obedece (não replica a regra localmente).
const canEdit = computed(
  () => p.allowEdit && getValueIgnoreCase(p.message, "canEdit") === true
);
const isEdited = computed(
  () => getValueIgnoreCase(p.message, "isEdited") === true
);
const editedTitle = computed(() => {
  const updatedAt = getValueIgnoreCase(p.message, "updatedAt");
  if (!updatedAt) return "Mensagem editada";
  return `Editada em ${Utilities.formatDateWithTime(
    updatedAt,
    "YYYY-MM-DDTHH:mm:ss",
    "DD/MM/YYYY [às] HH:mm"
  )}`;
});

// ✅ Mensagem sanitizada com DOMPurify + hyperlink
const mensagemSanitizada = computed(() => {
  const rawMessage = p.message?.Message || "";
  return DOMPurify.sanitize(rawMessage);
});

const isAutomaticMessage = computed(
  () =>
    p.message.Doctor == null &&
    p.message.Professional == null &&
    p.message.Origin !== "Patient" &&
    p.message.Origin !== 1
);

const messageOrigin = computed(() => {
  let result = "";

  if (isAutomaticMessage.value) {
    result = "Mensagem Automática";
  } else if (p.message.Origin === "Professional" || p.message.Origin === 2) {
    if (
      p.message.Professional != null &&
      getValueIgnoreCase(p.message.Professional, "Name") != null
    ) {
      result = getValueIgnoreCase(p.message.Professional, "Name");
    }
  }
  return result;
});

const CALL_MESSAGE_TYPES = Object.freeze({
  FINISHED: "VideoCallFinished",
  REJECTED: "VideoCallRejected",
  NOT_ANSWERED: "VideoCallNotAnswered"
});

const isCallEvent = computed(() =>
  Object.values(CALL_MESSAGE_TYPES).includes(p.message.Type)
);

const callEventParts = computed(() => {
  const raw = (p.message?.Message ?? "").trim();
  const match = raw.match(/^(.*?)\s*[-•]\s*(dura[cç][aã]o:?\s*)?(.+)$/i);
  if (match) {
    return {
      label: match[1].trim(),
      duration: `Duração: ${match[3].trim()}`
    };
  }
  return {
    label: raw,
    duration: p.message?.Duration ? `Duração: ${p.message.Duration}` : ""
  };
});

const showMessageDate = computed(() => {
  return (
    !p.lastDate ||
    moment(p.lastDate, "YYYY-MM-DD").unix() <
      moment(p.message.DateAndTime, "YYYY-MM-DD").unix()
  );
});

function onClick() {
  create({
    component: LilaImg,
    componentProps: {
      src: p.message.AttachedDocument,
      style: {
        width: "40%",
        alingSelf: "center",
        margin: "auto"
      }
    }
  });
}
</script>

<style>
.lila-chat-message {
  width: 100%;
}

.lila-chat-message .message-origin.is-automatic {
  font-weight: 800;
}

.lila-chat-message figure {
  cursor: pointer;
}

.lila-chat-message .doctor-message {
  margin-left: auto;
}

.lila-chat-message .call-event-message,
.lila-chat-message .call-event-message * {
  cursor: default !important;
  user-select: none;
}

.lila-chat-message .call-event-message {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 8px auto;
  padding: 12px 52px 18px 16px;
  background: #f0edf8;
  border-radius: 12px;
  color: #7c6aaa;
  font-size: 14px;
  width: fit-content;
  max-width: 90%;
}

.lila-chat-message .call-event-message__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #ffffff;
  color: #7c6aaa;
}

.lila-chat-message .call-event-message__body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: 2px;
}

.lila-chat-message .call-event-message__label {
  font-weight: 600;
  color: #4a3f6b;
}

.lila-chat-message .call-event-message__duration {
  font-size: 12px;
  color: #a8a8a8;
}

.lila-chat-message .call-event-message__time {
  position: absolute;
  right: 12px;
  bottom: 6px;
  color: #a8a8a8;
  font-size: 11px;
}

/* ---- Ação de editar no rodapé do balão ---- */
.lila-chat-message .message-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.lila-chat-message .message-edit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  opacity: 0.6;
  cursor: pointer;
  transition: opacity 0.15s ease, background-color 0.15s ease;
}

.lila-chat-message .message-edit-btn svg {
  width: 15px;
  height: 15px;
  display: block;
}

/* Cores adaptadas a cada balão (paleta do projeto) */
.lila-chat-message .doctor-message .message-edit-btn {
  color: #ffffff;
}

.lila-chat-message .doctor-message .message-edit-btn:hover {
  opacity: 1;
  background-color: rgba(255, 255, 255, 0.18);
}

.lila-chat-message .patient-message .message-edit-btn {
  color: #9a397d;
}

.lila-chat-message .patient-message .message-edit-btn:hover {
  opacity: 1;
  background-color: rgba(154, 57, 125, 0.12);
}

.lila-chat-message .message-edited-tag {
  font-size: 12px;
  font-style: italic;
  opacity: 0.7;
}
</style>
